import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { useToast } from "../hooks/use-toast";
import { 
  ArrowLeft, 
  Upload, 
  Eye, 
  Download, 
  CreditCard, 
  Palette,
  Type,
  QrCode,
  Sparkles,
  Settings
} from "lucide-react";
import { mockDesignTemplates, mockFonts, mockApi } from "../mock";
import DesignCatalog from "./DesignCatalog";
import EnhancedCard3DPreview from "./design/EnhancedCard3DPreview";
import CardCanvas, { CardCanvasViewer } from "./CardCanvas";
import AssetGallery from "./AssetGallery";
import FontPairingPanel from "./FontPairingPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { fontPairingsApi } from "../services/api";
import { Image as ImageIcon } from "lucide-react";
import ProfessionalCardDesigner from "./design/ProfessionalCardDesigner";
import PaymentForm from "./payment/PaymentForm";

const BusinessCardCreator = () => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const backgroundInputRef = useRef(null);
  const [currentStep, setCurrentStep] = useState("info");
  const [logoLibraryOpen, setLogoLibraryOpen] = useState(false);
  const [backgroundLibraryOpen, setBackgroundLibraryOpen] = useState(false);
  const [fontPairings, setFontPairings] = useState([]);
  const [selectedFontPairing, setSelectedFontPairing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mockDesignTemplates, setMockDesignTemplates] = useState([]);

  useEffect(() => {
    mockApi.fetchTemplates().then(setMockDesignTemplates);
  }, []);
  
  const [cardData, setCardData] = useState({
    companyName: "",
    personalName: "",
    customerName: "",
    jobTitle: "",
    phone: "",
    email: "",
    website: "",
    address: "",
    slogan: "",
    logo: null,
    logoUrl: "",
    customPhoto: null,
    templateId: 1,
    fontId: 1,
    orientation: "horizontal",
    fontSettings: {
      companyName: { font: 'Inter', size: 16, weight: '700', color: '#000000' },
      personalName: { font: 'Inter', size: 12, weight: '600', color: '#000000' },
      jobTitle: { font: 'Inter', size: 10, weight: '400', color: '#333333' },
      contactInfo: { font: 'Inter', size: 8, weight: '400', color: '#333333' }
    }
  });

  const handleInputChange = (field, value) => {
    setCardData(prev => ({ ...prev, [field]: value }));
  };

  const handleCardUpdate = (updatedCard) => {
    setCardData(updatedCard);
  };

  // Load font pairings once · WPS-018
  React.useEffect(() => {
    fontPairingsApi.list().then(r => {
      setFontPairings(r || []);
      if (r && r.length > 0 && !selectedFontPairing) {
        setSelectedFontPairing(r[0]);
        handleInputChange('fontPairingSlug', r[0].slug);
      }
    });
    // eslint-disable-next-line
  }, []);

  const handleBackgroundUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 5MB", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const result = await mockApi.uploadLogo(file);   // same endpoint, just different field
      if (result.success) {
        setCardData(prev => ({ ...prev, backgroundUrl: result.url }));
        toast({ title: "Background uploaded", description: "Applied as card background" });
      }
    } catch (error) {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 5MB",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const result = await mockApi.uploadLogo(file);
      if (result.success) {
        setCardData(prev => ({
          ...prev,
          logo: file,
          logoUrl: result.url
        }));
        toast({
          title: "Logo uploaded successfully",
          description: "Your logo has been added to the business card"
        });
      }
    } catch (error) {
      toast({
        title: "Upload failed",
        description: "Failed to upload logo. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCard = async () => {
    if (!cardData.companyName || !cardData.personalName || !cardData.email) {
      toast({
        title: "Missing information",
        description: "Please fill in company name, personal name, and email",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const result = await mockApi.saveBusinessCard(cardData);
      if (result.success) {
        toast({
          title: "Business card saved",
          description: "Your business card has been created successfully"
        });
        setCurrentStep("preview");
      }
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Failed to save business card. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    // WPS-017 · Client may only download a WATERMARKED PROOF (not print-grade).
    // The print-ready CMYK file is admin-only and only accessible after order approval.
    setLoading(true);
    try {
      const result = await mockApi.generatePDF(cardData);
      if (result.success) {
        toast({
          title: "Proof PDF ready",
          description: "This watermarked proof is for review only. The print-ready file is exclusive to Wunli.com and prepared by our atelier team after you Approve to Print."
        });
        const base = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '');
        const link = document.createElement('a');
        link.href = result.url?.startsWith('http') ? result.url : `${base}${result.url}`;
        link.download = `wunli-proof-${(cardData.companyName || 'card').replace(/\s+/g, '-').toLowerCase()}.pdf`;
        link.click();
      }
    } catch (error) {
      toast({
        title: "Proof generation failed",
        description: "Failed to generate proof. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplate = mockDesignTemplates.find(t => t.id === cardData.templateId);
  const selectedFont = mockFonts.find(f => f.id === cardData.fontId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
              <div className="flex items-center space-x-2">
                <div className="bg-gradient-to-r from-emerald-500 to-blue-600 p-2 rounded-lg">
                  <CreditCard className="h-5 w-5 text-white" />
                </div>
                <span className="text-lg font-bold text-gray-900">Professional Card Creator</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Creation Panel */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs value={currentStep} onValueChange={setCurrentStep}>
              <TabsList className="grid w-full grid-cols-6 bg-white shadow-sm border">
                <TabsTrigger value="info" className="flex items-center space-x-1 text-xs">
                  <Type className="h-3 w-3" />
                  <span className="hidden md:inline">Info</span>
                </TabsTrigger>
                <TabsTrigger value="design" className="flex items-center space-x-1 text-xs">
                  <Palette className="h-3 w-3" />
                  <span className="hidden md:inline">Design</span>
                </TabsTrigger>
                <TabsTrigger value="typography" className="flex items-center space-x-1 text-xs">
                  <Sparkles className="h-3 w-3" />
                  <span className="hidden md:inline">Typography</span>
                </TabsTrigger>
                <TabsTrigger value="customize" className="flex items-center space-x-1 text-xs">
                  <Settings className="h-3 w-3" />
                  <span className="hidden md:inline">Customize</span>
                </TabsTrigger>
                <TabsTrigger value="preview" className="flex items-center space-x-1 text-xs">
                  <Eye className="h-3 w-3" />
                  <span className="hidden md:inline">Preview</span>
                </TabsTrigger>
                <TabsTrigger value="payment" className="flex items-center space-x-1 text-xs">
                  <CreditCard className="h-3 w-3" />
                  <span className="hidden md:inline">Payment</span>
                </TabsTrigger>
              </TabsList>

              {/* Information Tab · compact iPhone-first */}
              <TabsContent value="info" className="space-y-3">
                <Card>
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="flex items-center space-x-2 text-base">
                      <Type className="h-4 w-4 text-emerald-600" />
                      <span>Business Info</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 px-4 pb-4 pt-1">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="companyName" className="text-xs font-medium">Company *</Label>
                        <Input id="companyName" placeholder="Your business"
                          value={cardData.companyName}
                          onChange={(e) => handleInputChange("companyName", e.target.value)}
                          className="h-9 text-sm" />
                      </div>
                      <div>
                        <Label htmlFor="personalName" className="text-xs font-medium">Your name *</Label>
                        <Input id="personalName" placeholder="Full name"
                          value={cardData.personalName}
                          onChange={(e) => handleInputChange("personalName", e.target.value)}
                          className="h-9 text-sm" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="jobTitle" className="text-xs font-medium">Title</Label>
                      <Input id="jobTitle" placeholder="e.g. Owner · Master Plumber"
                        value={cardData.jobTitle}
                        onChange={(e) => handleInputChange("jobTitle", e.target.value)}
                        className="h-9 text-sm" />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="email" className="text-xs font-medium">Email *</Label>
                        <Input id="email" type="email" placeholder="you@biz.com"
                          value={cardData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          className="h-9 text-sm" />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-xs font-medium">Phone</Label>
                        <Input id="phone" placeholder="+1 555 000 0000"
                          value={cardData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          className="h-9 text-sm" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="website" className="text-xs font-medium">Website</Label>
                        <Input id="website" placeholder="yourbiz.com"
                          value={cardData.website}
                          onChange={(e) => handleInputChange("website", e.target.value)}
                          className="h-9 text-sm" />
                      </div>
                      <div>
                        <Label htmlFor="address" className="text-xs font-medium">Service area</Label>
                        <Input id="address" placeholder="Dallas, TX"
                          value={cardData.address}
                          onChange={(e) => handleInputChange("address", e.target.value)}
                          className="h-9 text-sm" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="slogan" className="text-xs font-medium">Tagline / Slogan</Label>
                      <Input id="slogan" placeholder="Short memorable line"
                        value={cardData.slogan}
                        onChange={(e) => handleInputChange("slogan", e.target.value)}
                        className="h-9 text-sm" />
                    </div>

                    <div className="pt-2 border-t space-y-3">
                      {/* TWO buttons side by side · Logo + Background separated · WPS-018 */}
                      <div className="grid grid-cols-2 gap-3">

                        {/* LOGO column */}
                        <div className="border border-gray-200 p-2">
                          <div style={{
                            fontSize: '9px', fontFamily: 'JetBrains Mono, monospace',
                            letterSpacing: '0.12em', textTransform: 'uppercase',
                            color: '#64748b', marginBottom: '6px',
                          }}>① Logo</div>
                          <div className="flex flex-col gap-1.5">
                            <Button
                              variant="outline" size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={loading}
                              className="h-7 px-2 text-[10px] justify-start"
                            >
                              <Upload className="h-3 w-3 mr-1" />Upload my logo
                            </Button>
                            <Button
                              variant="outline" size="sm"
                              onClick={() => setLogoLibraryOpen(true)}
                              className="h-7 px-2 text-[10px] justify-start"
                            >
                              <Sparkles className="h-3 w-3 mr-1" />Pick from library
                            </Button>
                          </div>
                          {cardData.logoUrl && (
                            <div className="mt-2 flex items-center gap-1">
                              {cardData.logoUrl.startsWith('lucide:') ? (
                                <div className="w-7 h-7 border bg-white flex items-center justify-center text-[9px] font-mono">
                                  {cardData.logoUrl.replace('lucide:', '').slice(0, 4)}
                                </div>
                              ) : (
                                <img src={cardData.logoUrl} alt="Logo"
                                  className="w-7 h-7 object-contain border bg-white" />
                              )}
                              <button
                                onClick={() => handleInputChange('logoUrl', '')}
                                className="text-[9px] text-red-600 hover:underline"
                              >remove</button>
                            </div>
                          )}
                        </div>

                        {/* BACKGROUND column */}
                        <div className="border border-gray-200 p-2">
                          <div style={{
                            fontSize: '9px', fontFamily: 'JetBrains Mono, monospace',
                            letterSpacing: '0.12em', textTransform: 'uppercase',
                            color: '#64748b', marginBottom: '6px',
                          }}>② Background</div>
                          <div className="flex flex-col gap-1.5">
                            <Button
                              variant="outline" size="sm"
                              onClick={() => backgroundInputRef.current?.click()}
                              disabled={loading}
                              className="h-7 px-2 text-[10px] justify-start"
                            >
                              <Upload className="h-3 w-3 mr-1" />Upload my background
                            </Button>
                            <Button
                              variant="outline" size="sm"
                              onClick={() => setBackgroundLibraryOpen(true)}
                              className="h-7 px-2 text-[10px] justify-start"
                            >
                              <ImageIcon className="h-3 w-3 mr-1" />Pick from library
                            </Button>
                          </div>
                          {cardData.backgroundUrl && (
                            <div className="mt-2 flex items-center gap-1">
                              <img src={cardData.backgroundUrl.startsWith('http') ? cardData.backgroundUrl :
                                `${(process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '')}${cardData.backgroundUrl}`}
                                alt="BG"
                                className="w-7 h-7 object-cover border bg-white" />
                              <button
                                onClick={() => setCardData(prev => ({ ...prev, backgroundUrl: '' }))}
                                className="text-[9px] text-red-600 hover:underline"
                              >remove</button>
                            </div>
                          )}
                        </div>
                      </div>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <input
                        ref={backgroundInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleBackgroundUpload}
                        className="hidden"
                      />
                    </div>

                    {/* Logo Library Dialog · WPS-010 */}
                    <Dialog open={logoLibraryOpen} onOpenChange={setLogoLibraryOpen}>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-amber-600" />
                            Wunli Icon Library · Logos
                          </DialogTitle>
                          <p className="text-xs text-gray-600 mt-1">
                            Pick a vector icon as your logo · scales perfectly to print.
                          </p>
                        </DialogHeader>
                        <AssetGallery
                          defaultType="icon"
                          onSelect={(asset) => {
                            handleInputChange('logoUrl', asset.rgbPath);
                            setLogoLibraryOpen(false);
                          }}
                        />
                      </DialogContent>
                    </Dialog>

                    {/* Background Library Dialog · WPS-018 */}
                    <Dialog open={backgroundLibraryOpen} onOpenChange={setBackgroundLibraryOpen}>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <ImageIcon className="h-4 w-4 text-blue-600" />
                            Wunli Background Library · Patterns
                          </DialogTitle>
                          <p className="text-xs text-gray-600 mt-1">
                            Subtle atelier patterns · CMYK 300 DPI · print-safe.
                          </p>
                        </DialogHeader>
                        <AssetGallery
                          defaultType="background"
                          onSelect={(asset) => {
                            setCardData(prev => ({ ...prev, backgroundUrl: asset.rgbPath }));
                            setBackgroundLibraryOpen(false);
                          }}
                        />
                      </DialogContent>
                    </Dialog>

                    <div className="pt-4">
                      <Button 
                        onClick={() => setCurrentStep("design")}
                        className="w-full bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700"
                      >
                        Continue to Templates
                        <Sparkles className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Templates Tab */}
              <TabsContent value="design">
                <DesignCatalog
                  templates={mockDesignTemplates}
                  fonts={mockFonts}
                  selectedTemplateId={cardData.templateId}
                  selectedFontId={cardData.fontId}
                  onTemplateSelect={(id) => {
                    handleInputChange("templateId", id);
                    // Apply template default colors + slogan to cardData for live preview
                    const t = mockDesignTemplates.find(tp => tp.id === id);
                    if (t) {
                      if (t.categorySlug && !cardData.categorySlug) {
                        handleInputChange("categorySlug", t.categorySlug);
                      }
                      if (t.defaultSlogan && !cardData.slogan) {
                        handleInputChange("slogan", t.defaultSlogan);
                      }
                    }
                  }}
                  onFontSelect={(id) => handleInputChange("fontId", id)}
                  onContinue={() => setCurrentStep("customize")}
                  customerCategory={cardData.categorySlug}
                  customerData={cardData}
                />
              </TabsContent>

              {/* Customize Tab */}
              <TabsContent value="customize">
                <ProfessionalCardDesigner
                  cardData={cardData}
                  onCardUpdate={handleCardUpdate}
                  selectedTemplate={selectedTemplate}
                />
                <div className="mt-6">
                  <Button 
                    onClick={() => setCurrentStep("preview")}
                    className="w-full bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700"
                  >
                    Continue to Preview
                    <Eye className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              {/* Preview Tab · WPS-018 · CardCanvasViewer with guides */}
              <TabsContent value="preview">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-blue-600" />
                        Final Preview · 1:1 atelier proof
                      </div>
                    </CardTitle>
                    <p className="text-xs text-gray-600 mt-1">
                      Toggle guides to see print boundaries · red trim line · blue safe zone
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardCanvasViewer
                      cardData={{
                        ...cardData,
                        colors: (selectedTemplate && selectedTemplate.colors) || cardData.colors || ['#0a0a0a', '#c9a961'],
                      }}
                      tier={cardData.tier || 'signature'}
                      watermark={true}
                      allowTierSwitch={true}
                      allowOrientationSwitch={true}
                      fontPairing={selectedFontPairing}
                    />

                    <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t">
                      <div className="flex gap-2">
                        <Button onClick={handleSaveCard} variant="outline" disabled={loading}>
                          {loading ? 'Saving…' : 'Save draft'}
                        </Button>
                        <Button onClick={handleGeneratePDF} variant="outline" disabled={loading}>
                          <Download className="h-4 w-4 mr-2" />
                          Download Proof PDF
                        </Button>
                      </div>
                      <Button
                        onClick={() => setCurrentStep("payment")}
                        className="bg-blue-700 hover:bg-blue-800"
                      >
                        ✓ Approve to Print · Continue to Payment
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Typography Tab · WPS-018 */}
              <TabsContent value="typography">
                <FontPairingPanel
                  selectedSlug={selectedFontPairing?.slug || cardData.fontPairingSlug}
                  onSelect={(p) => {
                    setSelectedFontPairing(p);
                    handleInputChange('fontPairingSlug', p.slug);
                  }}
                  customerCategory={cardData.categorySlug}
                  customerData={cardData}
                  showLivePreview={true}
                />
              </TabsContent>

              {/* Payment Tab */}
              <TabsContent value="payment" className="space-y-6">
                <PaymentForm
                  orderData={{
                    cardData: cardData,
                    template: selectedTemplate,
                    amount: 52.97
                  }}
                  onPaymentSuccess={(paymentResult) => {
                    // WPS-001 · Payment now triggers SUBMITTED status backend-side.
                    // Order enters atelier pre-production queue · Carlos reviews before sending to 4over.
                    toast({
                      title: "✓ Approved to Print",
                      description: `Transaction ${paymentResult.transactionId?.slice(0,12)} · Your design has entered the Wunli atelier pre-production queue. Our team will review it personally before sending to print. You will receive an email when it ships.`
                    });
                    setTimeout(() => {
                      const customerAuth = localStorage.getItem('customerAuth');
                      if (customerAuth) {
                        window.location.href = '/dashboard';
                      } else {
                        window.location.href = '/';
                      }
                    }, 3500);
                  }}
                  onCancel={() => setCurrentStep("preview")}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Live Preview Sidebar · WPS-006 CardCanvas v3 · 1:1 real render */}
          <div className="space-y-6">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Eye className="h-5 w-5 text-blue-600" />
                  <span>Live Preview · 1:1</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{
                  background: '#f1f5f9',
                  padding: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                  border: '1px solid #e2e8f0',
                }}>
                  <CardCanvas
                    cardData={{
                      companyName: cardData.companyName || 'Your Company',
                      personalName: cardData.personalName || cardData.customerName || 'Your Name',
                      jobTitle: cardData.jobTitle || '',
                      email: cardData.email || '',
                      phone: cardData.phone || '',
                      website: cardData.website || '',
                      address: cardData.address || '',
                      slogan: cardData.slogan || '',
                      logoUrl: cardData.logoUrl || '',
                      backgroundUrl: cardData.backgroundUrl || '',
                      colors: (selectedTemplate && selectedTemplate.colors) || ['#0a0a0a', '#c9a961', '#ffffff'],
                      orientation: cardData.orientation || 'horizontal',
                      qrPosition: cardData.qrPosition || 'back',
                      qrPayload: cardData.website || cardData.phone,
                      tier: cardData.tier || 'signature',
                      backDesign: cardData.backDesign || { enabled: true, layout: 'minimal-text+qr', showQR: true },
                    }}
                    tier={cardData.tier || 'signature'}
                    watermark={true}
                    scale={0.85}
                    side="front"
                    showStockBadge={false}
                    fontPairing={selectedFontPairing}
                  />
                </div>
                <div className="mt-4 text-xs text-gray-500 text-center font-mono uppercase tracking-wider">
                  <p>Wunli Official · 3.375" × 2.125"</p>
                  <p>Bleed 0.0625" · CMYK 300 DPI · {cardData.orientation || 'horizontal'}</p>
                  <p style={{ color: '#c9a961', fontWeight: 700, marginTop: '4px' }}>PROOF · NOT FOR PRINT</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessCardCreator;